import { lazy } from 'react';
import { Trans } from '@lingui/react/macro';
import { getLabel } from '../../LanguageConstant';
import IntegrationsApi from '../../api/IntegrationsApi';
import WorkpageApi from '../../api/WorkpageApi';
import useResourceLoader from '../../util/useResourceLoader';
import Workpage from '../../components/Workpage/index';
import Loader from '../../components/Loader';

import SwitchBoard from '../../components/SwitchBoard';
import OrgUnitInstitutions from './OrgUnitInstitutions';
import WorkpageShellSkeleton from 'src/components/WorkpageShellSkeleton';
const SLASetting = lazy(() => import('./SLASetting'));
const Forms = lazy(() => import('./Forms'));
const Lists = lazy(() => import('./Lists'));
const ObjectSchemas = lazy(() => import('./ObjectSchema'));
const Sequences = lazy(() => import('./Sequences'));
const OrganizationUnits = lazy(() => import('./OrganizationalUnit'));
const OrganizationUnitTypes = lazy(() => import('./OrganizationalUnitType'));
const WorkflowIndex = lazy(() => import('./Workflow'));
const Export = lazy(() => import('./Export'));
const Integrations = lazy(() => import('./Integrations'));
const ListInvoiceItem = lazy(() => import('./Invoce/ListInvoiceItem'));
const Workpages = lazy(() => import('./Workpages'));
const Roles = lazy(() => import('./Roles'));
const Users = lazy(() => import('./Users'));
const Reports = lazy(() => import('./Reports'));
const Jobs = lazy(() => import('./Jobs'));
const Documents = lazy(() => import('./Documents'));
const Faqs = lazy(() => import('./Faq/index'));
const ThemeEditor = lazy(() => import('./ThemeEditor'));
const DocumentTemplates = lazy(() => import('./DocumentTemplate'));
const WorkflowNotifications = lazy(() => import('./WorkflowNotifications'));
const PopupNotifications = lazy(() => import('./Notifications'));

function AdminWorkpageTray({ workpageId, workpage: propWorkpage }) {
  const [workpage, loadingWorkpage] = useResourceLoader(async () => {
    if (propWorkpage.definition != null) return propWorkpage;
    return workpageId ? WorkpageApi.get({ id: workpageId }) : null;
  }, [workpageId, propWorkpage]);

  if (loadingWorkpage) {
    return <WorkpageShellSkeleton />;
  }

  if (!workpage) {
    return (
      <div>
        <Trans>Workpage not found</Trans>
      </div>
    );
  }

  return <Workpage schema={workpage.definition} scope={{ workpage }} />;
}

const getSystemSettingTabs = ({ ENABLE_JOBS_SETTINGS, ENABLE_DOCUMENTS_SETTINGS, adminWorkpages = [] }) => {
  const labels = getLabel();

  const adminWorkpageTabs = {};
  if (adminWorkpages?.length) {
    adminWorkpages.forEach(workpage => {
      const tabKey = workpage.slug;
      adminWorkpageTabs[tabKey] = {
        icon: workpage.image?.icon ?? 'Gauge',
        title: workpage.name,
        // TODO move this to workpage definition...
        // Checked against the untranslated base name (falling back to the
        // possibly-already-localized `name` for workpages with no i18n yet) —
        // `workpage.name` itself may already be displayed in the active locale,
        // and this grouping must not change when that display language does.
        group: (workpage.definition?.i18n?.name?.es ?? workpage.name).includes('Schedules de Inventario')
          ? 'workflowConfiguration'
          : 'administration',
        component: () => <AdminWorkpageTray workpageId={workpage.id} workpage={workpage} />,
        order: workpage.order,
      };
    });
  }

  const TAB_GROUPS = {
    administration: {
      name: labels.administration,
      icon: 'phosphor-ShieldDuotone',
      order: 1,
    },
    workflowConfiguration: {
      name: labels.workflowConfiguration,
      icon: 'phosphor-FlowArrowDuotone',
      order: 3,
    },
    supportingTools: {
      name: labels.supportingTools,
      icon: 'phosphor-HandHeartDuotone',
      order: 4,
    },
    utilities: {
      name: labels.utilities,
      icon: 'phosphor-WrenchDuotone',
      order: 5,
    },
    adminWorkpages: {
      name: labels.adminWorkpages,
      icon: 'fa fa-',
      order: 2,
    },
  };

  const TABS = {
    undefined: {
      noTab: true,
      component: ({ setActiveTab }) => (
        <SwitchBoard className="settings-landing-page" title={labels.selectComponent} tabs={TABS} groups={TAB_GROUPS} setActive={setActiveTab} />
      ),
    },
    // administration
    'users-jedi': {
      icon: 'User',
      title: 'Usuarios',
      group: 'administration',
      permission: 'ManageUsersInUnit',
      jnx: 'user.user.jediId',
      component: () => <Loader />,
      onActivate({ loadFn }) {
        loadFn(async () => {
          const url = await IntegrationsApi.GetOperationUrl('jedi/jump-to-jedi/login');
          if (url) {
            window.open(url, '_blank');
          }
        });
      },
    },
    'organizational-unit-types': {
      icon: 'OrgUnitTypes',
      title: labels.organizationalUnitTypes,
      group: 'administration',
      component: OrganizationUnitTypes,
      permission: 'ManageOrganizationalUnits',
      order: 0,
    },
    'organizational-units': {
      icon: 'OrgUnits',
      title: labels.organizationalUnits,
      group: 'administration',
      component: OrganizationUnits,
      permission: 'ManageOrganizationalUnits',
      order: 0,
    },
    institutions: {
      icon: 'Institution',
      title: labels.institutions,
      group: 'administration',
      component: OrgUnitInstitutions,
      // Any of the three org-unit permissions, matching getOrgUnitAccess's canView: a contact-info
      // editor reaches the institutions in their scope from here.
      jnx:
        'user.permissions.ManageOrganizationalUnits or user.permissions.ViewInScopeOrganizationalUnits or user.permissions.EditInScopeOrganisationalUnitsContactInfo',
      order: 0,
    },
    users: {
      icon: 'phosphor-UsersThreeDuotone',
      title: labels.users,
      group: 'administration',
      component: Users,
      permission: 'ManageUsersInUnit',
      jnx: '$not(user.user.jediId)',
      order: 0,
    },
    notification: {
      icon: 'PopupNotification',
      title: labels.popupNotifications || 'Popup Notifications',
      group: 'administration',
      component: PopupNotifications,
      permission: 'ManageNotifications',
      order: 10,
    },
    // workflow config
    roles: {
      icon: 'phosphor-IdentificationBadgeDuotone',
      title: labels.roles,
      group: 'workflowConfiguration',
      component: Roles,
      permission: 'ManageRole',
      order: 0,
    },
    'object-schemas': {
      icon: 'phosphor-DatabaseDuotone',
      title: labels.objectTypes,
      group: 'workflowConfiguration',
      component: ObjectSchemas,
      permission: 'ManageWorkflow',
      order: 0,
    },
    forms: {
      icon: 'phosphor-TextboxDuotone',
      title: labels.forms,
      group: 'workflowConfiguration',
      component: Forms,
      permission: 'ManageWorkflow',
      order: 0,
    },
    workpage: {
      icon: 'phosphor-LayoutDuotone',
      title: labels.workpages,
      group: 'workflowConfiguration',
      component: Workpages,
      permission: 'ManageWorkflow',
      order: 10,
    },
    workflow: {
      icon: 'phosphor-FlowArrowDuotone',
      title: labels.workflow,
      group: 'workflowConfiguration',
      component: WorkflowIndex,
      permission: 'ManageWorkflow',
      order: 10,
    },
    'workflow-notifications': {
      icon: 'phosphor-BellRingingDuotone',
      title: labels.workflowNotifications || 'Workflow Notifications',
      group: 'workflowConfiguration',
      component: WorkflowNotifications,
      permission: 'ManageWorkflow',
      order: 10,
    },
    // supporting tools
    reports: {
      icon: 'phosphor-PresentationChartDuotone',
      title: labels.analytics,
      group: 'supportingTools',
      component: Reports,
      permission: 'ManageReports',
    },
    integrations: {
      icon: 'phosphor-PlugsConnectedDuotone',
      title: labels.integrations,
      group: 'supportingTools',
      component: Integrations,
      permission: 'ManageIntegrations',
    },
    lists: {
      icon: 'phosphor-ListDashesDuotone',
      title: labels.lists,
      group: 'supportingTools',
      component: Lists,
      permission: 'ManageWorkflow',
    },
    sequences: {
      icon: 'phosphor-ListNumbersDuotone',
      title: labels.sequences,
      group: 'supportingTools',
      component: Sequences,
      permission: 'ManageWorkflow',
    },
    'document-templates': {
      icon: 'phosphor-FileDashedDuotone',
      title: labels.documentTemplates,
      group: 'supportingTools',
      component: DocumentTemplates,
      permission: 'ManageWorkflow',
    },
    // misc
    faq: {
      icon: 'phosphor-SealQuestionDuotone',
      title: labels.faq,
      group: 'utilities',
      component: Faqs,
      permission: 'ManageFaq',
    },
    themeEditor: {
      icon: 'phosphor-PaintRollerDuotone',
      title: labels.themes,
      group: 'utilities',
      component: ThemeEditor,
      permission: 'ManageTheme',
    },
    export: {
      icon: 'phosphor-FileCloudDuotone',
      title: labels.export,
      group: 'utilities',
      component: Export,
      permission: 'ManageWorkflow',
    },
    ...(ENABLE_JOBS_SETTINGS && {
      jobs: {
        icon: 'Robot',
        title: labels.jobs,
        group: 'utilities',
        component: Jobs,
        permission: 'ManageWorkflow',
      },
    }),
    ...(ENABLE_DOCUMENTS_SETTINGS && {
      documents: {
        icon: 'fa fa-file-pdf',
        title: labels.documents,
        group: 'utilities',
        component: Documents,
        permission: 'ManageWorkflow',
      },
    }),
    ...adminWorkpageTabs,
  };

  return [TABS, TAB_GROUPS];
};

export default getSystemSettingTabs;
